<template>
  <section class="ma-8">
    <t-page-title title="Mapeamento | Editar">
      <template #prepend-custom-action>
        <import-seats-action v-if="model.id" :mapping="model" @import="fetch" />
      </template>
    </t-page-title>
    <v-row justify="center" align="center">
      <v-col cols="12">
        <v-card class="pa-8">
          <form-mapping
            v-model="model"
            :save-blocker="saveBlocker"
            @submit="submit"
            @image-change="onImageChange"
          />
        </v-card>
      </v-col>
      <v-col v-if="model.sectors" cols="12">
        <v-card class="pa-8">
          <form-sector
            v-model="model.sectors"
            :editing="true"
            :saved-ids="persistedIds"
            @delete="deleteSector"
          />
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-card class="pa-8">
          <v-card-title class="px-0 pt-0">
            Mapa de setores
          </v-card-title>
          <v-card-subtitle class="px-0 mb-4 text-wrap">
            Vincule as formas do SVG aos Setores e Seções e clique em Salvar. Setores e Seções novos só podem ser
            vinculados depois de salvos.
          </v-card-subtitle>
          <sector-map-linking
            v-if="linking.svg.value"
            :key="mapLoads"
            :linking="linking"
            :disabled="loader.loading"
            :file-name="`mapa-${model.name}.svg`"
          />
          <div v-else class="text-medium-emphasis">
            {{ mapMessage }}
          </div>
        </v-card>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import TPageTitle from '@/components/TPageTitle.vue'
import Mapping, { MappingPayload } from '@/types/Mapping'
import MappingService from '@/services/MappingService'
import { useSnackBar } from '@/stores/snackbar'
import { useLoader } from '@/stores/loader'
import { useRouter } from 'vue-router'
import FormMapping from './form-mapping.vue'
import FormSector from './form-sector.vue'
import ImportSeatsAction from './import-seats-action.vue'
import { mappingFormData } from '@/helpers/mappingForm'
import SectorMapLinking from './sector-map/sector-map-linking.vue'
import { computed, onBeforeMount, ref, type ComputedRef } from 'vue'
import Local from '@/types/Local'
import Sector from '@/types/Sector'
import { useDialog } from '@/stores/dialog'
import SectorService from '@/services/SectorService'
import { useSectorMapLinking, type LinkableSector } from '@/helpers/sectorMap/linking'

const snackbar = useSnackBar()
const loader = useLoader()
const router = useRouter()
const dialog = useDialog()

const model = ref<Mapping>({
  name: '',
  image_url: '',
  has_sector_selection: false,
  local: {
    id: '',
  } as Local,
  sectors: [] as Sector[],
} as Mapping)

onBeforeMount(() => fetch())

async function fetch() {
  try {
    loader.increment()

    const response = await (new MappingService()).show(router.currentRoute.value.params.id as string)

    model.value = response.data.data
    model.value.sectors = model.value.sectors?.sort((a, b) => a.order - b.order)
    persistedSectors.value = [ ...model.value.sectors ?? [] ]
    persistedIds.value = new Set(persistedSectors.value.flatMap(sector => [
      sector.id,
      ...sector.sections.map(section => section.id),
    ]))
  } catch (error) {
    snackbar.show(
      'Não foi possível buscar o mapeamento',
      'error',
    )

    throw error
  } finally {
    loader.decrement()
  }

  await loadStoredMap()
}

async function submit(payload: MappingPayload) {
  if ((model.value.sectors ?? []).length <= 0) {
    snackbar.show(
      'O Mapeamento precisa ter pelo menos um setor',
      'warning',
    )
    return
  }

  try {
    loader.increment()
    const image = linking.svg.value && (chosenImage.value || linking.changed.value)
      ? new File([ linking.exportSvg() ], 'mapa.svg', { type: 'image/svg+xml' })
      : payload.image

    model.value.sectors!.forEach((sector, index) => {
      sector.order = index
    })

    await (new MappingService()).update(model.value.id, mappingFormData(payload, image))

    persistedSectors.value = model.value.sectors ?? []

    snackbar.show(
      'Mapeamento editado com sucesso',
      'success',
    )

    router.push({ name: 'list-mappings' })
  } finally {
    loader.decrement()
  }
}

// So we know on edit mode if we should ask for confirmation when deleting them
const persistedSectors = ref<Sector[]>([])

async function deleteSector(sector: Sector) {
  if (!persistedSectors.value.includes(sector)) {
    model.value.sectors = model.value.sectors?.filter(s => s !== sector)
    return
  }

  const confirmed = await dialog.open({
    title: 'Confirmação de deleção.',
    message: 'Realmente deseja excluir este setor? Esta ação é irreversível',
  })

  if (!confirmed) return

  try {
    loader.increment()
    await (new SectorService()).destroy(sector.id)

    model.value.sectors = model.value.sectors?.filter(s => s !== sector)

    snackbar.show(
      'Setor excluído com sucesso.',
      'success',
    )
  } finally {
    loader.decrement()
  }
}

// Only persisted Sectors and Sections can be linked: the shape ids must match the API's
const persistedIds = ref<Set<string>>(new Set())

const linkableSectors: ComputedRef<LinkableSector[]> = computed(() => (model.value.sectors ?? [])
  .filter(sector => persistedIds.value.has(sector.id))
  .map(sector => ({
    id: sector.id,
    name: sector.name,
    sections: sector.sections.filter(section => persistedIds.value.has(section.id)),
  })))

const linking = useSectorMapLinking({ mappingId: () => model.value.id, sectors: () => linkableSectors.value })

/** Re-mounts the tool, which keeps its selection, for every map loaded */
const mapLoads = ref(0)
const NO_MAP = 'Envie um SVG para vincular as formas aos Setores e Seções.'
const mapMessage = ref(NO_MAP)
/** The SVG picked in the form, which replaces the stored map when saved */
const chosenImage = ref<File | null>(null)

const saveBlocker: ComputedRef<string | null> = computed(() => linking.errorCount.value > 0
  ? `Corrija ${linking.errorCount.value} erro(s) no mapa de setores antes de salvar. Avisos não bloqueiam.`
  : null)

function showMap(markup: string) {
  linking.load(markup)
  mapLoads.value++
}

async function loadStoredMap() {
  linking.clear()
  mapMessage.value = NO_MAP

  if (!model.value.image_url) return

  try {
    const response = await window.fetch(model.value.image_url, { cache: 'no-store' })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    showMap(await response.text())
  } catch {
    mapMessage.value = 'A imagem atual não pôde ser lida como SVG. Envie um SVG para vincular as formas.'
  }
}

async function onImageChange(file: File | null) {
  chosenImage.value = file

  if (!file) {
    await loadStoredMap()
    return
  }

  try {
    showMap(await file.text())
  } catch (error) {
    linking.clear()
    mapMessage.value = 'O arquivo escolhido não é um SVG válido.'
    snackbar.show(error instanceof Error ? error.message : 'Não foi possível ler o SVG', 'error')
  }
}
</script>
